import React, { useState } from 'react';
import {
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Alert, CircularProgress, Box, Chip
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';

const API_BASE = 'https://console.dreamtec.io';

/**
 * Delete button + confirmation for one build/package row.
 * If the backend says the build is still in use (409), it shows where,
 * and offers "Delete anyway" (force=true).
 *
 * Usage:
 *   <DeleteBuildButton build={build} onDeleted={refreshList} />
 */
export const DeleteBuildButton = ({ build, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inUse, setInUse] = useState(null); // 409 payload

  const close = () => {
    if (loading) return;
    setOpen(false); setError(null); setInUse(null);
  };

  const doDelete = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/app-versions/${build.id}${force ? '?force=true' : ''}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 409) {
        const body = await res.json();
        setInUse(body.detail);
        setLoading(false);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.detail === 'string' ? body.detail : 'Failed to delete build');
      }

      setLoading(false);
      setOpen(false);
      setInUse(null);
      onDeleted?.(build.id);
    } catch (e) {
      setLoading(false);
      setError(e.message);
    }
  };

  return (
    <>
      <Tooltip title="Delete">
        <IconButton size="small" color="error" onClick={() => setOpen(true)}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1A3A3F' }}>Delete build?</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700 }}>v{build.version_label} ({build.version_code})</Typography>
            {build.package_type && <Chip size="small" label={build.package_type} sx={{ fontWeight: 700 }} />}
          </Box>
          <Typography variant="body2" color="text.secondary">
            This permanently removes the record and the stored file. Devices will no longer be able to download it. This can't be undone.
          </Typography>

          {inUse && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{inUse.message}</Typography>
              {inUse.configs?.length > 0 && (
                <Typography variant="body2">
                  Active config: {inUse.configs.map((c) => `CID ${c.cid} (${c.environment})`).join(', ')}
                </Typography>
              )}
              {inUse.pinned_device_count > 0 && (
                <Typography variant="body2">Pinned devices: {inUse.pinned_device_count}</Typography>
              )}
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={close} disabled={loading}>Cancel</Button>
          {inUse ? (
            <Button color="error" variant="contained" disabled={loading} onClick={() => doDelete(true)}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Delete anyway'}
            </Button>
          ) : (
            <Button color="error" variant="contained" disabled={loading} onClick={() => doDelete(false)}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};